/**
 * Lead admin schemas (issue #21, spec §17/§18).
 *
 * Shared between:
 *   - `api/src/leads/admin/*` — request validation for the `/api/admin/leads`
 *     endpoints (list / get-by-id / patch),
 *   - `web/src/...` — TanStack Query hooks + the admin lead list / detail
 *     drawer use these types so the SPA + API agree on field names.
 *
 * Status workflow (issue #21 hard constraint):
 *   - `NEW → REVIEWED`, `NEW → REJECTED`,
 *   - `REVIEWED → CONTACTED`, `REVIEWED → REJECTED`,
 *   - `CONTACTED → QUALIFIED`, `CONTACTED → REJECTED`,
 *   - `QUALIFIED → CONTACTED` (re-engage),
 *   - `REJECTED` is terminal — no transitions out.
 *
 * The transition matrix is exported as a typed constant so the API service
 * and the SPA's status-change `<Select>` derive their valid-next-status
 * lists from the same source.
 */

import { z } from "zod";
import type { LeadStatus } from "../types/index.js";

/**
 * Mirror of Prisma's `LeadStatus` enum (`api/prisma/schema.prisma`). The
 * type lives in `shared/src/types/index.ts`; we re-declare the runtime
 * tuple here so the Zod enum + the SPA's transition map can iterate it.
 */
export const LEAD_STATUSES = ["NEW", "REVIEWED", "CONTACTED", "QUALIFIED", "REJECTED"] as const;
export const leadStatusSchema = z.enum(LEAD_STATUSES);

/**
 * Allowed transitions per spec / issue #21. Frozen so a runtime mutation
 * cannot accidentally widen the matrix; both the API (validation) and the
 * SPA (`<Select>` options) read the same shape.
 */
export const LEAD_STATUS_TRANSITIONS: Readonly<Record<LeadStatus, ReadonlyArray<LeadStatus>>> = {
  NEW: ["REVIEWED", "REJECTED"],
  REVIEWED: ["CONTACTED", "REJECTED"],
  CONTACTED: ["QUALIFIED", "REJECTED"],
  QUALIFIED: ["CONTACTED"],
  REJECTED: [],
};

/**
 * Pure helper used by the API service (transition guard) AND the SPA's
 * status-change select (to populate options). Lives here so the two surfaces
 * cannot drift.
 */
export function isAllowedLeadTransition(from: LeadStatus, to: LeadStatus): boolean {
  return LEAD_STATUS_TRANSITIONS[from].includes(to);
}

/**
 * Query-parameter schema for `GET /api/admin/leads`.
 *
 * Pagination defaults to `page=1`, `pageSize=20`, max 100 per
 * `standards/api-patterns.md`. Filters are all optional. Date filters are
 * coerced from ISO strings (the Zod schema's `coerce.date()` accepts the
 * exact shape the SPA sends from a MUI `DatePicker`).
 *
 * `q` is a free-text substring matched case-insensitively against `name`,
 * `email`, and `company` in the repository (Prisma `OR` + `mode:'insensitive'`).
 */
export const listLeadsQuerySchema = z
  .object({
    page: z.coerce.number().int().positive().default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(20),
    siteId: z.string().min(1).optional(),
    status: leadStatusSchema.optional(),
    /**
     * Inclusive lower bound on `createdAt`. `coerce.date()` accepts an ISO
     * string (`new Date(x).toISOString()` round-trips cleanly).
     */
    from: z.coerce.date().optional(),
    /**
     * EXCLUSIVE upper bound on `createdAt`. Choosing exclusive means a
     * caller passing `to=2026-05-07T00:00:00.000Z` gets results for
     * 2026-05-06 inclusive without 23:59:59 fence-post pain.
     */
    to: z.coerce.date().optional(),
    q: z.string().trim().min(1).max(200).optional(),
  })
  .strict();
export type ListLeadsQuery = z.infer<typeof listLeadsQuerySchema>;

/**
 * Body schema for `PATCH /api/admin/leads/:id`. Either `status`, `notes`,
 * or both. The `.refine` clause rejects an empty body (would be a no-op
 * write that still fires an audit row — better to 422 explicitly).
 *
 * `notes` is bounded because we don't want a single submission to swell
 * the row past Postgres's TOAST threshold (~2KB) where it stops fitting in
 * an in-page tuple. 5000 chars is way above any realistic operator note.
 */
export const updateLeadSchema = z
  .object({
    status: leadStatusSchema.optional(),
    notes: z.string().max(5000).optional(),
  })
  .strict()
  .refine((obj) => obj.status !== undefined || obj.notes !== undefined, {
    message: "Update must include status, notes, or both.",
  });
export type UpdateLeadInput = z.infer<typeof updateLeadSchema>;

/**
 * Wire shape returned from `GET /api/admin/leads` (list) and
 * `GET /api/admin/leads/:id` (detail). Timestamps are ISO strings; JSON
 * fields are kept opaque at the boundary (the SPA narrows them).
 *
 * `experimentVariantName` is non-null when the row's `experimentVariantId`
 * resolves to an `ExperimentVariant` row; the API issues the join
 * conditionally only on the detail endpoint.
 */
export type LeadAdminResponse = {
  id: string;
  siteId: string;
  pageId: string | null;
  status: LeadStatus;
  name: string | null;
  email: string | null;
  company: string | null;
  phone: string | null;
  message: string | null;
  formJson: unknown;
  sourceJson: unknown;
  visitorId: string | null;
  sessionId: string | null;
  experimentVariantId: string | null;
  /**
   * Resolved variant name. `null` when `experimentVariantId` is null OR the
   * lookup didn't resolve (the variant row was hard-deleted, etc.). The
   * list endpoint omits this surface to avoid an N+1 join — only the
   * detail endpoint populates it.
   */
  experimentVariantName: string | null;
  notes: string | null;
  createdAt: string;
};
